import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Wine, Loader, Search } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";
const S = {
  page:{minHeight:"100vh",background:"#F2F5EC",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif",padding:"20px 0"},
  box:{background:"#EDF1E4",border:"1px solid #252525",borderRadius:14,padding:"36px 32px",width:"100%",maxWidth:480},
  label:{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",display:"block",marginBottom:6},
  input:{width:"100%",background:"#E8EDE0",border:"1px solid #222",borderRadius:8,padding:"11px 14px",fontSize:13,color:"#2C3820",fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"},
  btn:{width:"100%",padding:"12px",background:"#6B2737",border:"none",borderRadius:8,color:"#DCE4EC",fontSize:14,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:20},
  tab:(a)=>({flex:1,padding:"8px",background:a?"#E2D8DA":"transparent",border:a?"1px solid #2E1E22":"1px solid #222",borderRadius:7,color:a?"#886070":"#6A7E58",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}),
};

export default function SignupPage() {
  const [step,setStep]=useState(1); // 1=personal, 2=winery
  const [form,setForm]=useState({full_name:"",email:"",password:"",winery_action:"claim",winery_id:"",winery_name:"",road_slug:"vienna-venice",winery_description:"",winery_address:"",organic:false,price_tier:2,consent:false});
  const [wineries,setWineries]=useState([]);
  const [wineriesError,setWineriesError]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const { register } = useAuth("winery_owner");
  const navigate = useNavigate();
  const setField=(k,v)=>setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    fetch(`${API}/manage/wineries`).then(r=>r.json()).then(data=>{const list=Array.isArray(data)?data:[];setWineries(list);if(!list.length)setWineriesError(true);}).catch(()=>setWineriesError(true));
  },[]);

  const submit = async e => {
    e.preventDefault(); setError("");
    if (!form.consent) { setError("Please accept the Privacy Policy and Terms to continue."); return; }
    setLoading(true);
    try { await register(form); navigate("/manage"); }
    catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={S.box}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <Wine size={28} color="#6B2737" style={{marginBottom:10}}/>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#1E2C14",margin:"0 0 4px"}}>Register your winery</h1>
          <p style={{fontSize:12,color:"#6A7E58",margin:0}}>Step {step} of 2</p>
        </div>

        {error && <div style={{background:"rgba(107,39,55,0.1)",border:"1px solid rgba(107,39,55,0.2)",borderRadius:7,padding:"8px 12px",marginBottom:14,fontSize:12,color:"#886070"}}>{error}</div>}

        <form onSubmit={step===1?(e=>{e.preventDefault();setStep(2);}):submit}>
          {step===1 && <>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Full name</label>
              <input style={S.input} value={form.full_name} onChange={e=>setField("full_name",e.target.value)} placeholder="Maria Müller" required/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={form.email} onChange={e=>setField("email",e.target.value)} placeholder="you@winery.com" required/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Password</label>
              <input style={S.input} type="password" value={form.password} onChange={e=>setField("password",e.target.value)} placeholder="Min. 8 characters" minLength={8} required/>
            </div>
            <button type="submit" style={S.btn}>Continue →</button>
          </>}

          {step===2 && <>
            <div style={{display:"flex",gap:8,marginBottom:20}}>
              <button type="button" onClick={()=>setField("winery_action","claim")} style={S.tab(form.winery_action==="claim")}>Find existing winery</button>
              <button type="button" onClick={()=>setField("winery_action","create")} style={S.tab(form.winery_action==="create")}>Register new winery</button>
            </div>

            {form.winery_action==="claim" && <>
              {wineriesError && <div style={{fontSize:12,color:"#886070",marginBottom:8}}>Could not load wineries list.</div>}
              <label style={S.label}>Select your winery</label>
              <select style={{...S.input,marginBottom:12}} value={form.winery_id} onChange={e=>setField("winery_id",e.target.value)}>
                <option value="">— Select winery —</option>
                {wineries.map(w=><option key={w.id} value={w.id}>{w.name} ({w.road_slug})</option>)}
              </select>
              <p style={{fontSize:11,color:"#6A7E58",marginBottom:0}}>You will be linked as the owner. Only unclaimed wineries can be selected.</p>
            </>}

            {form.winery_action==="create" && <>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Winery name *</label>
                <input style={S.input} value={form.winery_name} onChange={e=>setField("winery_name",e.target.value)} placeholder="e.g. Weingut Mustermann" required/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Wine road</label>
                <select style={S.input} value={form.road_slug} onChange={e=>setField("road_slug",e.target.value)}>
                  <option value="ar-valley">Ahr Valley</option>
                  <option value="vienna-venice">Vienna – Venice</option>
                  <option value="malopolska">Małopolska</option>
                </select>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Address</label>
                <input style={S.input} value={form.winery_address} onChange={e=>setField("winery_address",e.target.value)} placeholder="Street, City, Country"/>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Description</label>
                <textarea style={{...S.input,minHeight:70,resize:"vertical"}} value={form.winery_description} onChange={e=>setField("winery_description",e.target.value)}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <input type="checkbox" id="org" checked={form.organic} onChange={e=>setField("organic",e.target.checked)}/>
                <label htmlFor="org" style={{fontSize:13,color:"#4A5E38",cursor:"pointer"}}>Certified organic</label>
              </div>
            </>}

            <label style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:11,color:"#6A7E58",marginTop:16,cursor:"pointer"}}>
              <input type="checkbox" checked={form.consent} onChange={e=>setField("consent",e.target.checked)} style={{marginTop:2}}/>
              <span>
                I agree to the <Link to="/terms" target="_blank" style={{color:"#886070"}}>Terms of Service</Link> and{" "}
                <Link to="/privacy" target="_blank" style={{color:"#886070"}}>Privacy Policy</Link>.
              </span>
            </label>

            <div style={{display:"flex",gap:8,marginTop:16}}>
              <button type="button" onClick={()=>setStep(1)} style={{...S.btn,background:"#E8EDE0",border:"1px solid #252525",color:"#5A6E48",marginTop:0}}>← Back</button>
              <button type="submit" style={{...S.btn,marginTop:0}} disabled={loading}>
                {loading?<Loader size={14}/>:null} Complete registration
              </button>
            </div>
          </>}
        </form>

        <p style={{textAlign:"center",fontSize:12,color:"#6A7E58",marginTop:20}}>
          Already registered? <Link to="/manage/login" style={{color:"#886070",textDecoration:"none"}}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
